/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Loading 클래스를 정의한다.
 *
 * @file /src/elements/loading/Loading.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 6. 20.
 */
import * as parent from '@base/Element';
import { Component } from '@base/Component';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { Language, LanguageText } from '@utils/Language';
import './Loading.css';

export type IndicatorType = 'column' | 'box' | 'circle' | 'dot' | 'atom';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 인디케이터 타입
     */
    indicator?: IndicatorType;

    /**
     * 로딩아이콘 및 로딩메시지의 위치방향
     */
    direction?: 'column' | 'row';

    /**
     * 로딩메시지
     */
    text?: LanguageText;

    /**
     * 로딩 메시지 정렬
     */
    textAlign?: 'left' | 'center' | 'right';

    /**
     * 백그라운드 비활성화효과를 추가할지 여부
     */
    background?: boolean;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Loading extends parent.Element {
    /**
     * 엘리먼트 타입
     */
    type = 'loading';

    /**
     * 인디케이터 타입
     */
    indicator: IndicatorType = 'column';

    /**
     * 로딩아이콘 및 로딩메시지의 위치방향
     */
    direction: 'column' | 'row' = 'row';

    /**
     * 로딩 메시지 정렬
     */
    textAlign: 'left' | 'center' | 'right' = 'center';

    /**
     * 로딩메시지
     */
    text: LanguageText = null;

    /**
     * 백그라운드 비활성화효과를 추가할지 여부
     */
    background: boolean = true;

    /**
     * 텍스트 DOM
     */
    $text: Dom = null;

    /**
     * 인디케이터 DOM
     */
    $indicator: Dom = null;

    /**
     * 로딩 엘리먼트를 정의한다.
     *
     * @param properties - 엘리먼트 초기설정
     */
    constructor(public properties: Properties<Loading> = null) {
        super(properties);

        this.indicator = this.properties.indicator;
        this.direction = this.properties.direction ?? 'column';
        this.text = this.properties.text ?? null;
        this.textAlign = this.properties.textAlign ?? 'center';
        this.background = this.properties.background !== false;
    }

    /**
     * 텍스트 DOM 을 가져온다.
     *
     * @returns 텍스트 DOM
     */
    $getText(): Dom {
        if (this.$text === null) {
            this.$text = Html.create('div', { 'data-role': 'text' });
        }

        return this.$text;
    }

    /**
     * 로딩메시지를 설정한다.
     *
     * @param text - 로딩메시지
     */
    async setText(text: LanguageText | null): Promise<void> {
        this.text = text;

        if (text === null) {
            this.$getText().empty();
        } else {
            this.$getText().html(await Language.resolveText(text));
        }
    }

    /**
     * 인디케이터 DOM 을 가져온다.
     *
     * @returns 인디케이터 DOM
     */
    $getIndicator(): Dom {
        if (this.$indicator === null) {
            this.$indicator = Html.create('div', { 'data-role': 'indicator' });
        }

        return this.$indicator;
    }

    /**
     * 인디케이터를 설정한다.
     *
     * @param indicator - 인디케이터 타입
     */
    async setIndicator(indicator: IndicatorType): Promise<void> {
        this.indicator = indicator;

        const $indicator = this.$getIndicator();
        if ($indicator.getAttr('data-type') != indicator) {
            $indicator.setAttr('data-type', indicator);

            switch (this.indicator) {
                case 'column':
                case 'atom':
                    $indicator.html('<i></i><i></i><i></i>');
                    break;

                case 'dot':
                    $indicator.html('<i></i><i></i><i></i><i></i>');
                    break;

                case 'box':
                case 'circle':
                    $indicator.html('<i></i>');
                    break;
            }
        }
    }

    /**
     * 로딩메시지를 닫는다.
     */
    close(): void {
        this.hide();
    }

    /**
     * 로딩창을 랜더링한다.
     */
    async render(): Promise<void> {
        if (this.isRendered() == true) {
            return;
        }

        if (this.isHidden() == true) {
            this.hide();
        }

        this.$getElement().addClass(this.textAlign);
        if (this.background == true) {
            this.$getElement().addClass('background');
        }

        const $box = Html.create('div', { 'data-role': 'box' });
        $box.addClass(this.direction);

        const $indicator = this.$getIndicator();
        this.setIndicator(this.indicator);
        $box.append($indicator);

        if (this.text !== null) {
            const $text = this.$getText();
            if (this.text !== null) {
                await this.setText(this.text);
            }
            $box.append($text);
        }

        this.$getElement().append($box);

        await this.onRender();
    }
}
